'use client'

import React from "react"

import { useState, useRef, useEffect } from 'react'
import { Send, Bot, User, Code, Palette, Boxes, Search, Sparkles, Loader2, FileCode, Trash2, RefreshCw, List, Globe, ImagePlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { useChatStore, AGENTS, type AgentType, type ChatMessage } from '@/lib/chat-store'
import { useAiCodeAssistant } from '@/hooks/useAiCodeAssistant'
import { useFileSync } from '@/hooks/useFileSync'
import { useLoadFiles } from '@/hooks/useLoadFiles'
import { useLoadMessages } from '@/hooks/useLoadMessages'
import { useActiveThemeId } from '@/hooks/useActiveThemeId'
import { useParams, useNavigate } from 'react-router-dom'
import { DirectUpload } from '@rails/activestorage'

const AgentIcons: Record<AgentType, React.ElementType> = {
  coder: Code,
  designer: Palette,
  architect: Boxes,
  reviewer: Search,
}

function AgentBadge({ agent }: { agent: AgentType }) {
  const config = AGENTS[agent]
  const Icon = AgentIcons[agent]

  if (!config) return null
  
  return (
    <span className={cn(
      "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary",
      config.color
    )}>
      <Icon className="h-3 w-3" />
      {config.name}
    </span>
  )
}

function AgentActionDisplay({ action }: { action: { agent: AgentType; type: string; message?: string; fileChanges?: { path: string; action: string }[] } }) {
  const config = AGENTS[action.agent]
  const Icon = AgentIcons[action.agent]

  if (!config) return null
  
  return (
    <div className="flex items-start gap-2 py-2 px-3 bg-secondary/30 rounded-lg text-sm">
      <div className={cn("mt-0.5", config.color)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn("font-medium", config.color)}>{config.name}</span>
          {action.type === 'thinking' && (
            <span className="text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Thinking...
            </span>
          )}
          {action.type === 'writing' && (
            <span className="text-muted-foreground">Writing code...</span>
          )}
          {action.type === 'analyzing' && (
            <span className="text-muted-foreground">Analyzing...</span>
          )}
          {action.type === 'complete' && (
            <span className="text-green-400">Done</span>
          )}
        </div>
        {action.message && (
          <p className="text-muted-foreground mt-1">{action.message}</p>
        )}
        {action.fileChanges && action.fileChanges.length > 0 && (
          <div className="mt-2 space-y-1">
            {action.fileChanges.map((change, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <FileCode className="h-3 w-3" />
                <span className={cn(
                  change.action === 'create' && 'text-green-400',
                  change.action === 'modify' && 'text-amber-400',
                  change.action === 'delete' && 'text-red-400'
                )}>
                  {change.action === 'create' && '+'}
                  {change.action === 'modify' && '~'}
                  {change.action === 'delete' && '-'}
                </span>
                <span className="text-foreground">{change.path}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user'
  const isTool = message.role === 'tool'
  
  // Tool messages displayed as small system notifications
  if (isTool) {
    return (
      <div className="flex gap-2 py-2 px-3 my-2 items-center bg-secondary/50 rounded-lg border border-border/50">
        <Sparkles className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">{message.content}</p>
        </div>
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    )
  }
  
  return (
    <div className={cn(
      "flex gap-3 py-4",
      isUser ? "flex-row-reverse" : "flex-row"
    )}>
      <div className={cn(
        "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
        isUser ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"
      )}>
        {isUser ? (
          <User className="h-4 w-4" />
        ) : (
          <Bot className="h-4 w-4" />
        )}
      </div>
      
      <div className={cn(
        "flex-1 space-y-2 max-w-[85%]",
        isUser && "flex flex-col items-end"
      )}>
        {/* Display tool calls from agentActions (loaded from database) */}
        {message.agentActions && message.agentActions.length > 0 && !isUser && (
          <div className="space-y-1 w-full">
            {message.agentActions.map((action: any, i: number) => {
              const functionName = action.function_name
              const args = action.arguments || {}
              const result = action.result || {}
              
              let displayText = ''
              let icon = <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
              let colorClass = 'text-muted-foreground'
              
              if (functionName === 'create_file') {
                displayText = `Created file: ${args.path}`
                if (result.lines) displayText += ` (${result.lines} lines)`
                icon = <FileCode className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                colorClass = 'text-green-500'
              } else if (functionName === 'edit_file') {
                displayText = `Edited file: ${args.path}`
                if (result.old_lines && result.new_lines) {
                  displayText += ` (${result.old_lines} → ${result.new_lines} lines)`
                }
                if (result.diff_summary) {
                  const { additions, deletions, modifications } = result.diff_summary
                  const parts = []
                  if (additions > 0) parts.push(`+${additions}`)
                  if (deletions > 0) parts.push(`-${deletions}`)
                  if (modifications > 0) parts.push(`~${modifications}`)
                  if (parts.length > 0) displayText += ` (${parts.join(', ')})`
                }
                icon = <FileCode className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                colorClass = 'text-amber-500'
              } else if (functionName === 'delete_file') {
                displayText = `Deleted file: ${args.path}`
                if (result.lines_removed) displayText += ` (${result.lines_removed} lines removed)`
                icon = <Trash2 className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
                colorClass = 'text-red-500'
              } else if (functionName === 'read_file') {
                displayText = `Read file: ${args.path}`
                icon = <FileCode className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                colorClass = 'text-blue-500'
              } else if (functionName === 'list_files') {
                displayText = `Listed files in: ${args.directory || 'root'}`
                icon = <List className="h-3.5 w-3.5 flex-shrink-0 text-purple-500" />
                colorClass = 'text-purple-500'
              } else if (functionName === 'search_files' || functionName === 'GrepRepo' || functionName === 'SearchRepo') {
                // <CHANGE> show search tools in UI
                displayText = `Searched files`
                icon = <Search className="h-3.5 w-3.5 flex-shrink-0 text-indigo-500" />
                colorClass = 'text-indigo-500'
              } else if (functionName === 'fetch_from_web' || functionName === 'FetchFromWeb') {
                // <CHANGE> show web fetch tools in UI
                displayText = `Fetched URLs`
                icon = <Globe className="h-3.5 w-3.5 flex-shrink-0 text-cyan-500" />
                colorClass = 'text-cyan-500'
              } else {
                displayText = `Executed: ${functionName}`
              }
              
              return (
                <div key={i} className="flex gap-2 py-2 px-3 items-center bg-secondary/30 rounded-lg border border-border/50">
                  {icon}
                  <p className={cn("text-xs flex-1 font-medium", colorClass)}>{displayText}</p>
                </div>
              )
            })}
          </div>
        )}
        
        {/* Display message content if present */}
        {(message.content || message.imageUrl) && (
          <div className={cn(
            "rounded-2xl px-4 py-2 space-y-2",
            isUser 
              ? "bg-primary text-primary-foreground rounded-tr-sm" 
              : "bg-secondary text-secondary-foreground rounded-tl-sm"
          )}>
            {message.content && (
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            )}
            {message.imageUrl && (
              <img
                src={message.imageUrl}
                alt="Uploaded reference"
                className="max-h-48 rounded-md border border-border/60"
              />
            )}
            {message.isStreaming && (
              <span className="inline-block w-2 h-4 bg-current animate-pulse ml-1" />
            )}
          </div>
        )}
        
        <span className="text-xs text-muted-foreground">
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  )
}

export function ChatPanel() {
  const [input, setInput] = useState('')
  const [filesLoaded, setFilesLoaded] = useState(false)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { siteSlug, themeBuildId } = useParams<{ siteSlug: string; themeBuildId?: string }>()
  const navigate = useNavigate()
  const { themeId } = useActiveThemeId(siteSlug)
  const buildId = themeBuildId || null
  const chatEnabled = Boolean(buildId && themeId && siteSlug)
  
  // Use the real AI assistant hook
  const { isConnected, sendMessage: sendAiMessage, clearMessages: clearAiMessages } = useAiCodeAssistant({
    themeBuildId: buildId || undefined,
    enabled: chatEnabled
  })
  
  // Load initial messages from backend on mount
  useLoadMessages({
    siteId: siteSlug || '',
    themeId: themeId || '',
    themeBuildId: buildId || '',
    enabled: chatEnabled
  })
  
  // Load initial files from backend on mount
  // Set filesLoaded flag when complete
  useLoadFiles({
    siteId: siteSlug || '',
    themeId: themeId || '',
    themeBuildId: buildId || '',
    enabled: chatEnabled,
    onLoadComplete: () => {
      setFilesLoaded(true)
      console.log('Initial files loaded, auto-sync will now start')
    }
  })
  
  // File sync hook - REACTIVE sync on file changes with 2 second debounce
  // Only start reactive sync after files are loaded to prevent deleting existing files
  const { syncFiles, isSyncing } = useFileSync({
    siteId: siteSlug || '',
    themeId: themeId || '',
    themeBuildId: buildId || '',
    enabled: chatEnabled,
    debounceMs: 2000, // 2 seconds debounce - syncs 2 seconds after last change
    initialLoadComplete: filesLoaded
  })
  
  const { messages, isLoading, clearMessages } = useChatStore()

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  useEffect(() => {
    setFilesLoaded(false)
  }, [buildId])

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`
    }
  }, [input])

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    
    if ((!input.trim() && !selectedImage) || isLoading || isUploadingImage || !chatEnabled) return
    
    const userMessage = input.trim() || (selectedImage ? 'Please analyze this image.' : '')
    setInput('')
    
    // Send to real AI assistant via Action Cable
    let imageSignedId: string | undefined

    if (selectedImage) {
      setIsUploadingImage(true)
      try {
        imageSignedId = await new Promise<string>((resolve, reject) => {
          const upload = new DirectUpload(selectedImage, '/direct_uploads')
          upload.create((error, blob) => {
            if (error) {
              reject(error)
            } else {
              resolve(blob.signed_id)
            }
          })
        })
      } catch (error) {
        console.error('Failed to upload image:', error)
      } finally {
        setIsUploadingImage(false)
      }
    }

    await sendAiMessage(userMessage, {
      imageSignedId,
      imagePreviewUrl: imagePreviewUrl || undefined
    })
    handleClearImage()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleClearMessages = () => {
    clearMessages()
    clearAiMessages()
  }

  const handleSelectImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      return
    }

    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }

    setSelectedImage(file)
    setImagePreviewUrl(URL.createObjectURL(file))
    event.target.value = ''
  }

  const handleClearImage = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }
    setSelectedImage(null)
    setImagePreviewUrl(null)
  }
  
  const goToConversations = () => {
    navigate(`/sites/${siteSlug}/devEditor/conversations`)
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <span className="font-semibold">AI Assistant</span>
          {isConnected && (
            <span className="inline-flex h-2 w-2 rounded-full bg-green-500" title="Connected" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={goToConversations}
            title="View all conversations"
          >
            <List className="h-4 w-4" />
          </Button>
          <div className="flex -space-x-1">
            {(Object.keys(AGENTS) as AgentType[]).map((agent) => {
              const Icon = AgentIcons[agent]
              return (
                <div
                  key={agent}
                  className={cn(
                    "w-6 h-6 rounded-full bg-secondary flex items-center justify-center border-2 border-background",
                    AGENTS[agent].color
                  )}
                  title={AGENTS[agent].name}
                >
                  <Icon className="h-3 w-3" />
                </div>
              )
            })}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={syncFiles}
            disabled={isSyncing}
            title="Sync files to backend"
          >
            <RefreshCw className={cn("h-4 w-4", isSyncing && "animate-spin")} />
          </Button>
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleClearMessages}
              title="Clear chat"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 h-[calc(100vh-300px)]" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Bot className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold text-lg mb-2">AI-Powered Site Builder</h3>
            <p className="text-muted-foreground text-sm max-w-xs mb-6">
              Describe what you want to build and our AI agents will help create it.
            </p>
            <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
              {(Object.entries(AGENTS) as [AgentType, typeof AGENTS[AgentType]][]).map(([key, agent]) => {
                const Icon = AgentIcons[key]
                return (
                  <div
                    key={key}
                    className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50 text-xs"
                  >
                    <Icon className={cn("h-4 w-4", agent.color)} />
                    <div>
                      <div className="font-medium">{agent.name}</div>
                      <div className="text-muted-foreground text-[10px]">{agent.description}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="py-4">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-border">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe what you want to build..."
              className="min-h-[44px] max-h-[150px] resize-none pl-12 pr-12 bg-secondary border-0"
              rows={1}
              disabled={isLoading || !chatEnabled}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute left-2 bottom-2 h-8 w-8"
              onClick={() => fileInputRef.current?.click()}
              disabled={!chatEnabled || isUploadingImage}
              title="Upload reference image"
            >
              <ImagePlus className="h-4 w-4" />
            </Button>
            <Button
              type="submit"
              size="icon"
              className="absolute right-2 bottom-2 h-8 w-8"
              disabled={(!input.trim() && !selectedImage) || isLoading || isUploadingImage || !chatEnabled}
            >
              {isLoading || isUploadingImage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleSelectImage}
            />
          </div>
        </form>
        {imagePreviewUrl && (
          <div className="mt-3 flex items-center gap-3 rounded-lg border border-border bg-secondary/40 p-2">
            <img
              src={imagePreviewUrl}
              alt="Selected upload"
              className="h-12 w-12 rounded object-cover"
            />
            <div className="flex-1 text-xs text-muted-foreground">
              {selectedImage?.name || 'Selected image'}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleClearImage}
              title="Remove image"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  )
}
